import { useAuth, useSignIn, useUser } from '@clerk/clerk-react';
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Sign in.
 *
 * A Client Portal user gets a magic link; they were invited by their agency and never chose
 * Fig, so the fewest possible steps is the whole design. Both first factors are enabled on the
 * Clerk instance (`email_link` and `email_code`), and the email address decides nothing here -
 * the link works for either audience, and an agency user who lands in this app is told plainly
 * that their product is elsewhere rather than being left to guess.
 *
 * No password field, deliberately: sign-up with password is off on the instance and passwords
 * are not a first factor.
 */
export function SignIn() {
  const { isLoaded, signIn } = useSignIn();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const wrongApp = params.get('wrong-app') === '1';
  const handoffFailed = params.get('handoff') === 'failed';
  const handoffReason = params.get('reason');
  const { isLoaded: authLoaded, isSignedIn, signOut } = useAuth();
  const { user } = useUser();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    setStatus('sending');
    try {
      const attempt = await signIn.create({ identifier: email.trim() });
      const factor = attempt.supportedFirstFactors?.find((f) => f.strategy === 'email_link');
      if (!factor || !('emailAddressId' in factor)) {
        throw new Error('That address can’t sign in with a link. Ask your agency to re-invite you.');
      }
      const { startEmailLinkFlow } = signIn.createEmailLinkFlow();
      void startEmailLinkFlow({
        emailAddressId: factor.emailAddressId as string,
        redirectUrl: `${window.location.origin}/`,
      });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      // Clerk's errors are developer-facing. Show ours; keep theirs only when it is readable.
      const raw = (err as { errors?: { longMessage?: string }[] })?.errors?.[0]?.longMessage;
      setMessage(raw ?? (err as Error).message ?? 'We couldn’t send that link. Try again.');
    }
  }

  // ALREADY SIGNED IN, BUT HERE (2026-10-21). Rasel reached this screen from the app's "Open the
  // portal" with a Clerk session in place, typed his address, and Clerk answered "you're already
  // signed in" - with no way forward. A sign-in form can never work for someone who is signed in,
  // so they get the two things that can: carry on, or start again as somebody else.
  if (authLoaded && isSignedIn) {
    const who = user?.primaryEmailAddress?.emailAddress ?? 'your account';
    return (
      <main className="page page-centred">
        <Wordmark />
        <h1 className="page-title">You're signed in</h1>
        <p className="state-body">
          This browser is signed in as <strong>{who}</strong>
          {wrongApp
            ? '. This is the client portal; your agency account works in the Fig app on your phone.'
            : ", but Fig couldn't open your account just now."}
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
          {!wrongApp && <button className="btn btn-primary" onClick={() => window.location.replace('/')}>Try again</button>}
          <button className="btn" onClick={() => { void signOut({ redirectUrl: `${window.location.origin}/sign-in` }); }}>
            Sign out
          </button>
        </div>
      </main>
    );
  }

  if (status === 'sent') {
    return (
      <main className="page page-centred">
        <Wordmark />
        <h1 className="page-title">Check your email</h1>
        <p className="state-body">
          We sent a link to <strong>{email}</strong>. Open it on this device and you’re in — there’s
          no password to remember.
        </p>
        <p className="state-body state-quiet">
          Nothing arrived? It can take a minute. Check spam, or{' '}
          <button className="linkbtn" onClick={() => setStatus('idle')}>use a different address</button>.
        </p>
      </main>
    );
  }

  return (
    <main className="page page-centred">
      <Wordmark />
      <h1 className="page-title">Sign in</h1>
      {/* Someone arriving here was invited by their agency and may never have heard of Fig.
          One line of context costs nothing and answers the question they actually have. */}
      <p className="signin-lede">
        Your agency keeps the calls its assistant handled for you here — listen to them, read
        the transcripts, in your own time.
      </p>
      {handoffFailed && (
        <div className="banner" role="status">
          We couldn’t sign you in from the app automatically. Sign in here instead.
          {handoffReason && <span className="state-quiet"> ({handoffReason})</span>}
        </div>
      )}
      {wrongApp && (
        <div className="banner" role="status">
          This is the client portal. Your agency account works in the Fig app on your phone.
        </div>
      )}
      <form onSubmit={onSubmit} noValidate>
        <label className="field">
          <span className="field-label">Email address</span>
          <input
            className="input"
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-describedby={status === 'error' ? 'signin-error' : 'signin-hint'}
            aria-invalid={status === 'error'}
          />
        </label>
        <p id="signin-hint" className="hint">
          We’ll email you a link. No password needed.
        </p>
        {status === 'error' && (
          <p id="signin-error" className="hint hint-error" role="alert">{message}</p>
        )}
        <button className="btn btn-primary" type="submit" disabled={!isLoaded || status === 'sending' || email.trim() === ''}>
          {status === 'sending' ? 'Sending…' : 'Email me a link'}
        </button>
      </form>
    </main>
  );
}

/**
 * The Fig mark.
 *
 * Deliberately a mark and a name, not an illustration. Premium is restraint - the sign-in
 * screen's job is to be unmistakably somebody's product and then get out of the way, and
 * anything more here is ornament, which is the cheap imitation of quality.
 *
 * Not the AGENCY's branding: at this point nobody has signed in, so there is no agency to
 * read branding from. `GET /api/agency/branding` needs a token. The white-labelling begins
 * the moment they are through the door, which is the earliest it honestly can.
 */
function Wordmark() {
  return (
    <div className="wordmark">
      <span className="wordmark-mark" aria-hidden="true" />
      <span className="wordmark-name">Fig</span>
    </div>
  );
}
